import React, { memo } from 'react';
import { Handle, Position, useUpdateNodeInternals } from 'reactflow';
import { Key, Table as TableIcon, Hash, Type, Calendar, ToggleLeft, Braces, Lock } from 'lucide-react';

const getIconForType = (type) => {
    switch (type) {
        case 'number': return <Hash size={12} className="text-blue-500" />;
        case 'text': return <Type size={12} className="text-slate-500" />;
        case 'date': return <Calendar size={12} className="text-orange-500" />;
        case 'boolean': return <ToggleLeft size={12} className="text-green-500" />;
        case 'json': return <Braces size={12} className="text-purple-500" />;
        default: return <Type size={12} className="text-slate-500" />;
    }
};

const TableNode = ({ data, selected, id }) => {
    const updateNodeInternals = useUpdateNodeInternals();

    React.useEffect(() => {
        updateNodeInternals(id);
    }, [data.columns, id, updateNodeInternals]);

    const phiCount = data.columns.filter(c => c.isPd).length;

    return (
        <div className={`mr-2 bg-white rounded-xl shadow-sm border min-w-[240px] max-w-[320px] transition-all duration-200 ${
            selected ? 'ring-2 ring-blue-500 border-blue-500 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
        }`}>
            {/* Header */}
            <div className="px-3 py-2 flex items-center gap-2 border-b border-blue-700 bg-blue-600 rounded-t-xl">
                <div className="p-1 rounded bg-blue-500 text-white border border-blue-400">
                    <TableIcon size={14} />
                </div>
                <span className="text-sm font-bold text-white truncate flex-1" title={data.label}>
                    {data.label || 'Unnamed Table'}
                </span>
                {phiCount > 0 && (
                    <span className="flex items-center gap-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0">
                        <Lock size={8} />
                        {phiCount} PD
                    </span>
                )}
            </div>

            {/* Columns */}
            <div className="py-1 bg-white">
                {data.columns.map((col) => (
                    <div
                        key={col.name}
                        className={`group flex items-center px-1 py-1 cursor-pointer transition-colors ${
                            col.isPd ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (data.onColumnClick) data.onColumnClick(col.name);
                        }}
                    >
                        {/* Target Handle */}
                        <div className="w-4 h-4 flex items-center justify-center relative flex-shrink-0">
                            <Handle
                                type="target"
                                position={Position.Left}
                                id={`${col.name}-target`}
                                className="!w-3 !h-3 !bg-violet-500 !border-2 !border-white transition-colors hover:!bg-violet-600"
                                style={{ position: 'relative', left: 0, transform: 'none', top: 0 }}
                            />
                        </div>

                        {/* PK icon / spacer */}
                        <div className="w-4 flex items-center justify-center flex-shrink-0">
                            {col.isPrimaryKey ? (
                                <Key size={12} className="text-amber-500" />
                            ) : (
                                <div className="w-3" />
                            )}
                        </div>

                        {/* Column name */}
                        <span
                            className={`text-xs truncate flex-1 px-1 ${
                                col.isPd
                                    ? 'text-red-700 font-semibold'
                                    : col.isForeignKey
                                    ? 'text-blue-600 font-medium'
                                    : 'text-slate-700'
                            }`}
                            title={col.name}
                        >
                            {col.name}
                        </span>

                        {/* Type icon */}
                        <div title={col.type} className="flex-shrink-0 mr-1">
                            {getIconForType(col.type)}
                        </div>

                        {/* PD Lock Toggle — always visible, green = safe, red = PD active */}
                        <button
                            title={col.isPd ? '🔴 PD Applied — click to remove' : '🟢 No PD — click to mark as PD'}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (data.onPhiToggle) data.onPhiToggle(col.name);
                            }}
                            className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full border-2 transition-all duration-150 ${
                                col.isPd
                                    ? 'bg-red-500 border-red-400 text-white hover:bg-red-600'
                                    : 'bg-green-100 border-green-400 text-green-600 hover:bg-green-200 hover:border-green-500'
                            }`}
                        >
                            <Lock size={9} strokeWidth={2.5} />
                        </button>

                        {/* Source Handle */}
                        <div className="w-4 h-4 flex items-center justify-center relative flex-shrink-0 ml-1">
                            <Handle
                                type="source"
                                position={Position.Right}
                                id={`${col.name}-source`}
                                className="!w-3 !h-3 !bg-green-500 !border-2 !border-white transition-colors hover:!bg-green-600"
                                style={{ position: 'relative', right: 0, transform: 'none', top: 0 }}
                            />
                        </div>
                    </div>
                ))}
                {data.columns.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400 italic text-center">No columns defined</div>
                )}
            </div>

            {/* Footer — shows PD status */}
            <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100 flex justify-between items-center text-[10px] rounded-b-xl">
                <span className="text-slate-400">{data.columns.length} columns</span>
                <span className={`flex items-center gap-1 font-semibold ${phiCount > 0 ? 'text-red-500' : 'text-green-600'}`}>
                    <Lock size={9} />
                    {phiCount > 0 ? `${phiCount} PD Applied` : 'No PD'}
                </span>
            </div>
        </div>
    );
};

export default memo(TableNode);
